import { NavLink, Outlet, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { ClientAuthProvider, useClientAuth } from "../../auth/ClientAuthProvider";
import { useAuth } from "../../auth/useAuth";
import {
  getStoredActiveClientViewId,
  setStoredActiveClientViewId,
} from "../../hooks/useClientPortalDashboard";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import {
  CASAMAPA_ADMIN_PORTAL_BASE,
  MIDPOINT_MEDIA_ADMIN_PORTAL_BASE,
} from "../../constants/clientAdminPortalRoutes";
import { casamapaAdminPortalPath } from "../../constants/casamapaAdminPortalPaths";
import { CASAMAPA_ADMIN_PORTAL_I18N_MANIFEST } from "../../constants/casamapaAdminPortalI18n";
import IntlProvider from "../../intl/IntlProvider.jsx";
import { useOptionalLocale } from "../../intl/LocaleProvider.jsx";
import CasaMapaAdminPortalLocaleControls from "./CasaMapaAdminPortalLocaleControls";
import "../../styles/clientPortalDashboard.css";
import "../../styles/clientCommunityForm.css";
import "../../styles/clientPublishing.css";
import "../../styles/casamapaAdminPortal.css";
import "../../styles/midpointMediaAdmin.css";

function resolveActiveViewId(clientUser, routeViewId, { preferSlug, requirePreferredSlug } = {}) {
  const views = clientUser?.assignedClientViews || [];
  if (views.length === 0) {
    return null;
  }

  if (preferSlug) {
    const preferred = views.find((view) => view.slug === preferSlug);
    if (preferred) {
      return preferred.clientViewId;
    }
    if (requirePreferredSlug) {
      return null;
    }
  }

  if (routeViewId && views.some((view) => view.clientViewId === routeViewId)) {
    setStoredActiveClientViewId(routeViewId);
    return routeViewId;
  }

  const stored = getStoredActiveClientViewId();
  if (stored && views.some((view) => view.clientViewId === stored)) {
    return stored;
  }

  return views[0].clientViewId;
}

function isCasaMapaNavPathActive(pathname, prefixes = []) {
  return prefixes.some((prefix) => {
    if (!prefix) {
      return false;
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

function CasaMapaAdminNavItem({ item, pathname }) {
  const children = item.enabled
    ? (item.children || []).filter((child) => child.enabled && child.to)
    : [];
  const childActive = children.some((child) =>
    isCasaMapaNavPathActive(pathname, child.matchPrefixes || [child.to])
  );
  const sectionActive = isCasaMapaNavPathActive(pathname, item.matchPrefixes || [item.to]);

  const parentClassName = ({ isActive }) => {
    if (isActive && !childActive) {
      return "active";
    }
    if (sectionActive || childActive) {
      return "is-section";
    }
    return undefined;
  };

  const parent = item.enabled && item.to ? (
    <NavLink to={item.to} end={item.end} className={parentClassName}>
      <span className="nav-icon">{item.icon}</span>
      {item.label}
    </NavLink>
  ) : (
    <span className="nav-link-disabled" aria-disabled="true">
      <span className="nav-icon">{item.icon}</span>
      {item.label}
    </span>
  );

  if (children.length === 0) {
    return parent;
  }

  return (
    <div className="casamapa-admin-nav__group">
      {parent}
      <div className="casamapa-admin-nav__children">
        {children.map((child) => (
          <NavLink
            key={child.to}
            to={child.to}
            end={child.end}
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            {child.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function casaMapaAccountInitials(email) {
  const source = String(email || "CM").trim();
  const parts = source.split(/\s+|@/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function CasaMapaAdminSidebarAccountMenu({ email, signedInLabel, regionalSettingsLabel }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  return (
    <div className="casamapa-admin-sidebar__account" ref={menuRef}>
      <button
        type="button"
        className="casamapa-admin-sidebar__account-trigger"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className="casamapa-admin-sidebar__account-avatar" aria-hidden="true">
          {casaMapaAccountInitials(email)}
        </span>
        <span className="casamapa-admin-sidebar__account-meta">
          <span className="casamapa-admin-sidebar__account-label">{signedInLabel}</span>
          <strong>{email}</strong>
        </span>
      </button>
      {menuOpen ? (
        <div className="casamapa-admin-sidebar__account-menu" role="menu">
          <div className="casamapa-admin-sidebar__account-menu-prefs">
            <p className="casamapa-admin-sidebar__account-menu-prefs-label">
              {regionalSettingsLabel}
            </p>
            <CasaMapaAdminPortalLocaleControls variant="menu" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const ClientPortalShellContent = () => {
  const { clientUser, loading, isAuthenticated } = useClientAuth();
  const { isSuperAdmin, loading: platformAuthLoading } = useAuth();
  const { clientViewId: routeViewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = useClientPortalPaths();
  const isCasaMapaPortal = paths.base === CASAMAPA_ADMIN_PORTAL_BASE;
  const isMidpointMediaPortal = paths.base === MIDPOINT_MEDIA_ADMIN_PORTAL_BASE;
  const localeCtx = useOptionalLocale();
  const t = localeCtx?.t || ((key) => key);

  const activeViewId = useMemo(
    () =>
      resolveActiveViewId(clientUser, routeViewId, {
        preferSlug: isCasaMapaPortal
          ? "casamapa"
          : isMidpointMediaPortal
            ? "midpoint-media"
            : undefined,
        requirePreferredSlug: isCasaMapaPortal || isMidpointMediaPortal,
      }),
    [clientUser, routeViewId, isCasaMapaPortal, isMidpointMediaPortal]
  );

  const activeView = clientUser?.assignedClientViews?.find(
    (view) => view.clientViewId === activeViewId
  );
  const permissions = activeView?.assignment?.permissions;
  const canAccessPortal = isAuthenticated || isSuperAdmin;

  if (loading || (!isAuthenticated && platformAuthLoading)) {
    return (
      <div className="client-portal">
        <div className="main">
          <p className="empty-state">Loading…</p>
        </div>
      </div>
    );
  }

  if (!canAccessPortal) {
    return <Navigate to={paths.login} replace />;
  }

  if (
    clientUser?.mustChangePassword &&
    location.pathname !== paths.changePassword
  ) {
    return <Navigate to={paths.changePassword} replace />;
  }

  const signedInEmail = clientUser?.email || (isSuperAdmin ? "Platform administrator" : "");

  const viewBase = activeViewId ? paths.view(activeViewId) : null;
  const hasMultipleViews = (clientUser?.assignedClientViews?.length || 0) > 1;

  const midpointNavItems = [
        { to: paths.dashboard, label: "Overview", icon: "🏠", end: true, enabled: true },
        {
          to: `${paths.base}/crm`,
          label: "CRM",
          icon: "🎯",
          enabled: Boolean(viewBase && permissions?.viewOpportunities),
        },
        {
          to: viewBase ? `${viewBase}/opportunities` : null,
          label: "Pipeline",
          icon: "📌",
          enabled: Boolean(viewBase && permissions?.viewOpportunities),
        },
        {
          to: `${paths.base}/people`,
          label: "People",
          icon: "👤",
          enabled: true,
          end: true,
          matchPrefixes: [`${paths.base}/people`],
          children: [
            {
              to: `${paths.base}/people/applications`,
              label: "Applications",
              enabled: Boolean(permissions?.viewOpportunities),
            },
            {
              to: `${paths.base}/people/accounts`,
              label: "Accounts",
              enabled: Boolean(permissions?.manageIam || permissions?.manageSettings),
            },
            {
              to: `${paths.base}/people/schedules`,
              label: "Schedules",
              enabled: true,
            },
          ],
        },
        {
          to: `${paths.base}/infrastructure`,
          label: "Infrastructure",
          icon: "🖥️",
          enabled: true,
        },
        {
          to: `${paths.base}/projects`,
          label: "Projects",
          icon: "📋",
          enabled: true,
        },
        {
          to: viewBase ? `${viewBase}/analytics` : null,
          label: "Analytics",
          icon: "📊",
          enabled: Boolean(viewBase && permissions?.viewAnalytics),
        },
        {
          to: viewBase ? `${viewBase}/settings` : null,
          label: "Settings",
          icon: "⚙️",
          end: true,
          enabled: Boolean(viewBase && (permissions?.manageSettings || permissions?.manageIam)),
          matchPrefixes: viewBase ? [`${viewBase}/settings`] : [],
          children: [
            {
              to: viewBase ? `${viewBase}/settings` : null,
              label: "General",
              end: true,
              enabled: true,
            },
            {
              to: viewBase ? `${viewBase}/settings/notifications` : null,
              label: "Notifications",
              enabled: true,
            },
            {
              to: viewBase ? `${viewBase}/settings/audit` : null,
              label: "Audit",
              enabled: true,
            },
          ],
        },
      ];

  const navItems = isMidpointMediaPortal
    ? midpointNavItems
    : isCasaMapaPortal
    ? [
        {
          to: paths.dashboard,
          label: t("realEstate.casaMapa.admin.nav.dashboard"),
          icon: "🏠",
          end: true,
          enabled: true,
        },
        {
          to: viewBase ? `${viewBase}/opportunities` : null,
          label: t("realEstate.casaMapa.admin.nav.leads"),
          icon: "🎯",
          enabled: Boolean(viewBase && permissions?.viewOpportunities),
        },
        {
          to: casamapaAdminPortalPath("agencies"),
          label: t("realEstate.casaMapa.admin.nav.agencies"),
          icon: "🏢",
          enabled: true,
          children: [
            {
              to: casamapaAdminPortalPath("listings"),
              label: t("realEstate.casaMapa.admin.nav.listings"),
              enabled: true,
            },
            {
              to: casamapaAdminPortalPath("public-inventory"),
              label: t("realEstate.casaMapa.admin.nav.publicInventory"),
              enabled: true,
            },
            {
              to: casamapaAdminPortalPath("property-linking"),
              label: t("realEstate.casaMapa.admin.nav.propertyLinking"),
              enabled: true,
            },
          ],
        },
        {
          to: casamapaAdminPortalPath("analytics/website"),
          label: t("realEstate.casaMapa.admin.nav.analytics"),
          icon: "📊",
          enabled: Boolean(viewBase && permissions?.viewAnalytics),
          matchPrefixes: [
            casamapaAdminPortalPath("analytics"),
            casamapaAdminPortalPath("market-trends"),
          ],
          children: [
            {
              to: casamapaAdminPortalPath("market-trends"),
              label: t("realEstate.casaMapa.admin.nav.marketTrends"),
              enabled: Boolean(viewBase && permissions?.viewAnalytics),
            },
          ],
        },
        {
          to: casamapaAdminPortalPath("vendors"),
          label: t("realEstate.casaMapa.admin.nav.vendors"),
          icon: "🛠️",
          enabled: true,
        },
        {
          to: casamapaAdminPortalPath("iam"),
          label: t("realEstate.casaMapa.admin.nav.access"),
          icon: "🔐",
          enabled: Boolean(permissions?.manageIam),
        },
        {
          to: viewBase ? `${viewBase}/settings` : null,
          label: t("realEstate.casaMapa.admin.nav.settings"),
          icon: "⚙️",
          enabled: Boolean(viewBase && (permissions?.manageSettings || permissions?.manageIam)),
        },
      ]
    : [
        { to: paths.dashboard, label: "Dashboard", icon: "🏠", end: true, enabled: true },
        {
          to: viewBase ? `${viewBase}/communities` : null,
          label: "Communities",
          icon: "🏘️",
          enabled: Boolean(viewBase),
        },
        {
          to: viewBase ? `${viewBase}/courses` : null,
          label: "Courses",
          icon: "⛳",
          enabled: Boolean(viewBase),
        },
        {
          to: viewBase ? `${viewBase}/opportunities` : null,
          label: "Opportunities & Leads",
          icon: "🎯",
          enabled: Boolean(viewBase && permissions?.viewOpportunities),
        },
        {
          to: viewBase ? `${viewBase}/servicios` : null,
          label: "Proveedores de servicios",
          icon: "🛠️",
          enabled: Boolean(viewBase && isCasaMapaPortal),
        },
        {
          to: viewBase ? `${viewBase}/realtors-vendors` : null,
          label: "Realtors & Vendors",
          icon: "🤝",
          enabled: Boolean(viewBase),
        },
        {
          to: viewBase ? `${viewBase}/analytics` : null,
          label: "Analytics",
          icon: "📊",
          enabled: Boolean(viewBase && permissions?.viewAnalytics),
        },
        {
          to: viewBase ? `${viewBase}/publishing` : null,
          label: "Publishing",
          icon: "📝",
          enabled: Boolean(viewBase),
        },
        {
          to: viewBase ? `${viewBase}/settings` : null,
          label: "Settings",
          icon: "⚙️",
          enabled: Boolean(viewBase && (permissions?.manageSettings || permissions?.manageIam)),
        },
      ];

  const displayUrl = activeView?.publicUrl || activeView?.slug || null;

  const shellClassName = isCasaMapaPortal
    ? "client-portal client-portal--casamapa-admin"
    : isMidpointMediaPortal
      ? "client-portal client-portal--midpoint-admin"
      : "client-portal";

  return (
    <div className={shellClassName}>
      <div className="app-shell">
        <aside className={`sidebar${isCasaMapaPortal ? " casamapa-admin-sidebar" : ""}`}>
          <div className="brand">
            <div className="brand-mark">{isCasaMapaPortal ? "CM" : "M"}</div>
            <div className="brand-text">
              <strong>
                {isCasaMapaPortal
                  ? t("realEstate.casaMapa.admin.portalTitle")
                  : isMidpointMediaPortal
                    ? "Midpoint Media"
                    : "Midpoint Portal"}
              </strong>
              <span>
                {isCasaMapaPortal
                  ? t("realEstate.casaMapa.admin.internalWorkspaceLabel")
                  : isMidpointMediaPortal
                    ? "Operations"
                    : "Client Workspace"}
              </span>
            </div>
          </div>

          {!isCasaMapaPortal && !isMidpointMediaPortal ? (
            <div className="client-card">
              <div className="label">Active Client View</div>
              {activeView ? (
                <>
                  <div className="name">{activeView.name}</div>
                  {displayUrl ? <div className="url">{displayUrl}</div> : null}
                  {hasMultipleViews ? (
                    <select
                      className="view-selector"
                      aria-label="Select Client View"
                      value={activeViewId || ""}
                      onChange={(event) => {
                        setStoredActiveClientViewId(event.target.value);
                        if (location.pathname === paths.dashboard) {
                          navigate(paths.dashboard, { replace: true });
                          window.dispatchEvent(
                            new CustomEvent("client-portal-view-changed", {
                              detail: { clientViewId: event.target.value },
                            })
                          );
                        } else if (
                          activeViewId &&
                          location.pathname.startsWith(paths.view(activeViewId))
                        ) {
                          navigate(
                            location.pathname.replace(
                              paths.view(activeViewId),
                              paths.view(event.target.value)
                            ),
                            { replace: true }
                          );
                        } else {
                          navigate(paths.dashboard, { replace: true });
                        }
                      }}
                    >
                      {clientUser.assignedClientViews.map((view) => (
                        <option key={view.clientViewId} value={view.clientViewId}>
                          {view.name}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </>
              ) : (
                <div className="name">No view assigned</div>
              )}
            </div>
          ) : null}

          <nav
            className={`nav${isCasaMapaPortal ? " casamapa-admin-nav" : ""}`}
            aria-label={
              isCasaMapaPortal
                ? t("realEstate.casaMapa.admin.nav.ariaLabel")
                : isMidpointMediaPortal
                  ? "Midpoint Media operations navigation"
                  : "Client portal navigation"
            }
          >
            {isCasaMapaPortal ? (
              <p className="casamapa-admin-nav__label">{t("realEstate.casaMapa.admin.nav.section")}</p>
            ) : null}
            {navItems.map((item) =>
              isCasaMapaPortal || (isMidpointMediaPortal && item.children?.length) ? (
                <CasaMapaAdminNavItem key={item.label} item={item} pathname={location.pathname} />
              ) : item.enabled && item.to ? (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => (isActive ? "active" : undefined)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </NavLink>
              ) : (
                <span key={item.label} className="nav-link-disabled" aria-disabled="true">
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </span>
              )
            )}
          </nav>

          {isCasaMapaPortal ? (
            <div className="casamapa-admin-sidebar__footer">
              <CasaMapaAdminSidebarAccountMenu
                email={signedInEmail}
                signedInLabel={t("realEstate.casaMapa.admin.signedInLabel")}
                regionalSettingsLabel={t("realEstate.casaMapa.admin.regionalSettingsLabel")}
              />
            </div>
          ) : (
            <div className="sidebar-footer">
              Signed in as
              <br />
              <strong>{signedInEmail}</strong>
              {isMidpointMediaPortal ? (
                <>
                  <br />
                  <NavLink to={paths.logout}>Sign out</NavLink>
                </>
              ) : null}
            </div>
          )}
        </aside>

        <main className="main page-content">
          <Outlet context={{ activeViewId, activeView, permissions, portalPaths: paths }} />
        </main>
      </div>
    </div>
  );
};

const ClientPortalLayout = () => {
  const paths = useClientPortalPaths();
  const isCasaMapaPortal = paths.base === CASAMAPA_ADMIN_PORTAL_BASE;

  const shell = (
    <ClientAuthProvider>
      <ClientPortalShellContent />
    </ClientAuthProvider>
  );

  if (!isCasaMapaPortal) {
    return shell;
  }

  return (
    <IntlProvider surface="clientView" manifest={CASAMAPA_ADMIN_PORTAL_I18N_MANIFEST}>
      {shell}
    </IntlProvider>
  );
};

export default ClientPortalLayout;
